import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "./AdminDashboard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { User, Clock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAudit() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchLogs = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      let url = `${API}/admin/audit-logs?limit=100`;
      if (filter !== "all") url += `&entity_type=${filter}`;

      const response = await axios.get(url, { headers });
      setLogs(response.data.logs);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load audit logs");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, filter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatAction = (action) => {
    if (action === "login") return "Signed in";
    if (action.startsWith("status_change:")) {
      const change = action.replace("status_change:", "");
      const [from, to] = change.split("→");
      return (
        <span className="inline-flex items-center gap-1">
          <span className="text-[#A1A1AA]">{from?.trim()}</span>
          <ArrowRight className="w-3 h-3 text-[#D4D4D8]" />
          <span className="font-medium text-[#18181B]">{to?.trim()}</span>
        </span>
      );
    }
    return action;
  };

  const getEntityBadge = (type) => {
    const styles = {
      lead: "bg-blue-50 text-blue-700",
      prayer_request: "bg-purple-50 text-purple-700",
      admin: "bg-zinc-100 text-zinc-700"
    };
    const labels = {
      lead: "Lead",
      prayer_request: "Prayer",
      admin: "Admin"
    };
    return { style: styles[type] || styles.admin, label: labels[type] || type };
  };

  return (
    <AdminLayout currentPage="/admin/audit">
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-audit">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-[#18181B]">Audit Trail</h1>
          <p className="text-sm text-[#71717A] mt-1">Track administrative actions and changes</p>
        </div>

        {/* Filter */}
        <div className="bg-white border border-[#E4E4E7] p-4 mb-4">
          <div className="flex items-center gap-3">
            <Label className="text-xs font-medium text-[#71717A] uppercase tracking-wide whitespace-nowrap">
              Filter
            </Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full sm:w-40 h-9 border-[#E4E4E7]" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="lead">Leads</SelectItem>
                <SelectItem value="prayer_request">Prayers</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Audit List */}
        <div className="bg-white border border-[#E4E4E7]">
          {loading ? (
            <div className="p-8 text-center text-[#71717A] text-sm">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-[#71717A] text-sm">No audit logs found</div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Time</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">User</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Action</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => {
                      const badge = getEntityBadge(log.entity_type);
                      return (
                        <tr key={log.id || index} className="border-b border-[#E4E4E7] last:border-0 hover:bg-[#FAFAFA]">
                          <td className="py-3 px-4 text-sm text-[#A1A1AA] tabular-nums whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-[#F4F4F5] rounded-full flex items-center justify-center">
                                <User className="w-3 h-3 text-[#71717A]" />
                              </div>
                              <span className="text-sm font-medium text-[#18181B]">{log.admin_username}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${badge.style}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {formatAction(log.action)}
                          </td>
                          <td className="py-3 px-4 text-xs text-[#A1A1AA] font-mono">
                            {log.entity_id?.slice(0, 8)}...
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="sm:hidden divide-y divide-[#E4E4E7]">
                {logs.map((log, index) => {
                  const badge = getEntityBadge(log.entity_type);
                  return (
                    <div key={log.id || index} className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-[#F4F4F5] rounded-full flex items-center justify-center">
                            <User className="w-3 h-3 text-[#71717A]" />
                          </div>
                          <span className="text-sm font-medium text-[#18181B]">{log.admin_username}</span>
                        </div>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${badge.style}`}>
                          {badge.label}
                        </span>
                      </div>
                      <p className="text-sm text-[#52525B] mb-2">
                        {formatAction(log.action)}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-[#A1A1AA]">
                        <Clock className="w-3 h-3" />
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
